import { OndemandVideo } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useRenamePlaylist } from '~/api/playlists';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { InlineEditTitle, Toolbar } from '~/components/toolbar';
import { useFolderContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { i18n } from '~/lib/i18n';
import { usePlaylist } from '../context';

export interface PlaylistToolbarProps {
  actions?: ReactNode;
}

export const PlaylistToolbar = ({ actions }: PlaylistToolbarProps) => {
  const { playlist, playlistFolder } = usePlaylist();
  const { folderTree } = useFolderContext();
  const link = useLink();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [renamePlaylist] = useRenamePlaylist();
  const { t } = useTranslation();

  return (
    <Toolbar
      pageTitle={playlist.name}
      actions={actions}
      titleIcon={<OndemandVideo />}
      titleEditor={
        <InlineEditTitle
          inputId={`playlists-item-${playlist.id}-name`}
          value={playlist.name}
          update={(value) =>
            renamePlaylist({
              variables: { playlistId: playlist.id, name: value.trim() },
            })
          }
          tooltip={t('pages.playlists.detail.toolbar.edit_tooltip')}
          validationSchema={object({
            name: string()
              .required(i18n.t('pages.playlists.detail.toolbar.validation.name_required'))
              .trim(),
          })}
        />
      }
      breadcrumbsLabel={
        <FolderBreadcrumbs
          allItems={() =>
            navigate(
              link({
                pathname: `/playlists/${folderTree.id}`,
                search: searchParams.toString(),
              }),
            )
          }
          label={t('pages.playlists.all_playlists')}
          changeFolder={(id) =>
            navigate(link({ pathname: `/playlists/${id}`, search: searchParams.toString() }))
          }
          folder={playlistFolder}
          contentPage
        />
      }
    />
  );
};
